import type { PendingTask } from "@closer/shared";
import { PENDING_TASK_KIND_NAMES } from "@closer/shared";
import { Badge } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { relativeDateLabel } from "../../lib/relativeDate";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * "BENDEN BEKLENİYOR" — bu sözleşmede kullanıcının kendi payına düşen işler.
 *
 * ### ⚠ Ürün sahibinin şikâyetinin ölçülebilir yarısı
 *
 * *"İstediğim bilgiyi göremiyorum."* Detay ekranı sözleşmenin NE OLDUĞUNU (künye, taraf,
 * tarih) ve süreçlerin GENEL durumunu (Kritik Süreçler) söylüyordu; kullanıcının kendi
 * yapması gerekeni hiçbir yerde söylemiyordu. Hedef tasarımın aynı ekranında bu bir kart:
 * "Karşı Taraf Yanıtı Bekleniyor · 5 gün kaldı", "İmza Bekleniyor · 7 gün kaldı".
 *
 * ### ⚠ YENİ UÇ AÇILMADI
 *
 * `/api/pending-tasks` zaten kullanıcının KENDİ görevlerini `contractId` ile döndürüyor
 * (`lib/pendingTasks/query.ts`), ve sorgu anahtarı Üst Bar panelininkiyle AYNI — yani bu
 * blok ikinci bir istek atmıyor, panelin önbelleğini okuyor. Sunucu tarafında bir
 * `?contractId=` süzgeci daha zarif olurdu ama uç değişikliği ister; kullanıcının kendi
 * görev listesi zaten küçük ve kayıt erişimi projeksiyonun içinde uygulanıyor.
 *
 * ### ⚠ KRİTİK SÜREÇLER'LE ÇAKIŞMIYOR
 *
 * O panel "bu sözleşmenin süreçleri ne durumda" sorusunu cevaplıyor — herkes için, süreç
 * bazında. Bu blok "BANA ne düşüyor" sorusunu cevaplıyor — kişiye özel, görev bazında.
 * Aynı sözleşmede süreç yürüyor olup kullanıcıya hiçbir görev düşmemiş olabilir; tersi de
 * doğru.
 *
 * ### ⚠ BOŞKEN HİÇ ÇİZİLMİYOR
 *
 * Boş durum kartı YOK. Özet bölümü tam da boş kartlardan arındırıldığı için yeniden
 * düzenlendi (dokuz karttan yedisi "henüz yok" diyordu); "size düşen bir iş yok" kartı
 * aynı hatayı tekrarlardı. Uç düşerse de sessiz kalıyor: bu bir yardımcı özet, kanonik
 * yeri Bekleyen İşlemler sayfası ve Üst Bar paneli — burada bir hata kutusu çizmek,
 * kullanıcının çözemeyeceği bir uyarıyı asıl içeriğin üstüne koymak olurdu.
 */
export function ContractMyPendingTasks({ contractId }: { contractId: string }) {
  const { activeTenantId } = useAuth();

  const sorgu = useQuery({
    queryKey: tenantKey(activeTenantId!, "pending-tasks"),
    queryFn: async () =>
      (await api<PendingTask[]>("/api/pending-tasks", { tenantId: activeTenantId! })).data,
  });

  const gorevler = (sorgu.data ?? []).filter((g) => g.contractId === contractId);
  if (gorevler.length === 0) return null;

  return (
    <section
      data-testid="benden-bekleniyor"
      className="flex flex-col gap-2 rounded-xl border border-warning-muted bg-warning-soft p-4"
    >
      <h2 className="text-base font-semibold text-fg-heading">Benden bekleniyor</h2>
      <ul className="flex flex-col">
        {gorevler.map((gorev) => {
          const kalan = relativeDateLabel(gorev.dueAt);
          return (
            <li
              key={gorev.id}
              className="flex flex-wrap items-center justify-between gap-2 border-b border-warning-muted py-1.5 last:border-0"
            >
              <span className="flex flex-wrap items-center gap-2">
                {/* Tür ADI paylaşılan katalogdan; ekran kendi sözlüğünü tutmuyor. */}
                <Badge tone="warning">{PENDING_TASK_KIND_NAMES[gorev.kind]}</Badge>
                {/*
                  ⚠ TALEP EDEN — işin KİMDEN geldiği, ne olduğu kadar önemli.

                  Bu alan yazıldığında `pendingTaskSchema` onu tanımıyordu (sunucu
                  gönderiyor, sözleşme bilmiyordu) ve okumaktan kaçınılmıştı. CLOS-1112
                  (R5 §12.3) alanı tabana aldı; blok da diğer üç yüzeyle aynı hizaya geldi.

                  ⚠ Bilinmiyorsa satır BOŞ değil HİÇ çizilmiyor — "Talep eden: —" olmayan
                  bir bilgiyi varmış gibi gösterirdi.
                */}
                {gorev.requestedByName ? (
                  <span className="text-sm text-fg-muted">Talep eden: {gorev.requestedByName}</span>
                ) : null}
              </span>
              {/*
                ⚠ Son tarih MUTLAK + GÖRELİ birlikte: kullanıcı günü takvime işliyor,
                aciliyeti okuyor. Tarihsiz görevde ikisi de çizilmiyor — `dueAt` dört
                süreçte şemada zorunlu (ADR-0065) ama eski satırlar boş olabilir.
              */}
              {gorev.dueAt ? (
                <span className="text-xs text-fg-soft">
                  Son tarih {new Date(gorev.dueAt).toLocaleDateString("tr-TR")}
                  {kalan ? ` · ${kalan}` : ""}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
